import {
  surplusExplanationSchema,
  surplusSettingsIdentitySchema,
} from "@erp/shared-types";
import "zod/compile";
import { z } from "zod";

export const StockSurplusReasonSchema = z.enum([
  "no_snapshot",
  "missing_commitments",
  "missing_policy",
  "history_unavailable",
  "catchment_pending",
  "invalid_input",
]);
export type StockSurplusReason = z.infer<typeof StockSurplusReasonSchema>;

export const StockSurplusLocationSchema = z.object({
  warehouseId: z.string().uuid(),
  explanation: surplusExplanationSchema.optional(),
  status: z.enum(["calculated", "estimated", "unavailable"]),
  reason: StockSurplusReasonSchema.nullable(),
  snapshotDate: z.string().nullable(),
  onHand: z.number().nullable(),
  reserved: z.number().nullable(),
  salesOrderDemand: z.number().nullable(),
  stagedOutbound: z.number().nullable(),
  /** Before clamping at zero; negative values disclose a shortage. */
  uncommittedPosition: z.number().nullable(),
  available: z.number().nullable(),
  minimumProtection: z.number().nullable(),
  minimumSource: z.string().nullable(),
  policyId: z.string().uuid().nullable(),
  demandWarehouseIds: z.array(z.string().uuid()),
  /** Dates identify covered months, not months with a positive sale. */
  coveredMonths: z.array(z.string()),
  observedSales: z.number().nullable(),
  annualSales: z.number().nullable(),
  retained: z.number().nullable(),
  quantity: z.number().nullable(),
});
export type StockSurplusLocation = z.infer<typeof StockSurplusLocationSchema>;

/** Diagnostic only. This result never grants permission to transfer stock. */
export const StockSurplusLimitationSchema = z.enum([
  "customer_sales_coverage_unverified",
  "quantity_unit_unverified",
  "current_live_diagnostic",
  "engine_commitment_limitations",
  "source_overlap_conflict",
  "missing_source_identity",
  "source_incompatible",
  "restricted_catchment",
  "cross_location_quantity_unit_unverified",
  "aggregate_numeric_overflow",
]);
export type StockSurplusLimitation = z.infer<
  typeof StockSurplusLimitationSchema
>;

export const StockSurplusSchema = z.object({
  settingsIdentity: surplusSettingsIdentitySchema.optional(),
  rule: z.enum(["trailing_customer_sales_12m", "configured_composition"]),
  version: z.union([z.literal(1), z.literal(2)]),
  estimateMethod: z
    .enum([
      "covered_month_average_times_12",
      "covered_month_average_times_coverage",
    ])
    .nullable(),
  evaluatedAt: z.string(),
  demandScopeAsOf: z.string().nullable(),
  limitations: z.array(StockSurplusLimitationSchema),
  windowStart: z.string(),
  windowEndExclusive: z.string(),
  timezone: z.string(),
  unit: z.string().nullable(),
  salesBasis: z.string(),
  status: z.enum(["calculated", "estimated", "unavailable"]),
  quantity: z.number().nullable(),
  /** S / A, expressed as a percentage; null for A=0 or incomplete scope. */
  sharePercent: z.number().nullable(),
  available: z.number().nullable(),
  /** Never a full-scope result when any location is unavailable. */
  knownSubtotal: z.number().nullable(),
  evaluatedLocations: z.number().int().nonnegative(),
  locations: z.array(StockSurplusLocationSchema),
});
export type StockSurplus = z.infer<typeof StockSurplusSchema>;
